/**
 * ProShip Direct v2 — drag a file here, or click to browse.
 *
 * Every upload in this module takes one CSV, so the affordance is shared rather than rebuilt per
 * page. Click still works: drag-and-drop alone excludes keyboard users and anyone whose file is
 * two folders deep, so the zone is a real button with a real file input behind it.
 */
import React, { useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';

import { SUBTLE, NAVY, BORDER, LIGHT_BG, PRIMARY_BLUE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { RADIUS } from '../../../styles/foundation/spacing';

export default function FileDropZone({ onFile, hint, accept = '.csv,text/csv', disabled = false }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);

  const take = (file) => {
    if (file && !disabled) onFile(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setOver(false);
    take(e.dataTransfer?.files?.[0]);
  };

  return (
    <Box
      role='button'
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && !disabled && inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        px: 2,
        py: 3,
        textAlign: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        border: `1.5px dashed ${over ? PRIMARY_BLUE : BORDER}`,
        bgcolor: over ? `${PRIMARY_BLUE}0a` : LIGHT_BG,
        borderRadius: RADIUS.md,
        transition: 'border-color 120ms, background-color 120ms',
        '&:hover': disabled ? undefined : { borderColor: PRIMARY_BLUE },
      }}
    >
      <UploadFileOutlinedIcon sx={{ fontSize: 26, color: over ? PRIMARY_BLUE : SUBTLE }} />
      <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY }}>
        {over ? 'Drop to upload' : 'Drag your CSV here, or click to browse'}
      </Typography>
      {hint && (
        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, lineHeight: 1.5 }}>
          {hint}
        </Typography>
      )}
      <input
        ref={inputRef}
        type='file'
        accept={accept}
        hidden
        onChange={(e) => {
          take(e.target.files?.[0]);
          // Let the same file be picked again after a fix.
          e.target.value = '';
        }}
      />
    </Box>
  );
}
